import {compositionMaterialType} from "../data/CompositionMaterialType";
import {DEFAULT_TEXTURE_SCALE_MM} from "../pages/common/settings/module3d/detailTextures";

/** Слоты текстур композиции (GET composition + PATCH set-texture). */
export const COMPOSITION_TEXTURE_SLOTS = [
    {
        compositionMaterialType: "BODY_TOP",
        textureIdField: "bodyTopTextureId",
        label: "Текстура верха корпуса",
    },
    {
        compositionMaterialType: "BODY_BOTTOM",
        textureIdField: "bodyBottomTextureId",
        label: "Текстура низа корпуса",
    },
    {
        compositionMaterialType: "FRONT_TOP",
        textureIdField: "frontTopTextureId",
        label: "Текстура верха фасада",
    },
    {
        compositionMaterialType: "FRONT_BOTTOM",
        textureIdField: "frontBottomTextureId",
        label: "Текстура низа фасада",
    },
    {
        compositionMaterialType: "BACK_TOP",
        textureIdField: "backTopTextureId",
        label: "Текстура верха задней стенки",
    },
    {
        compositionMaterialType: "BACK_BOTTOM",
        textureIdField: "backBottomTextureId",
        label: "Текстура низа задней стенки",
    },
    {
        compositionMaterialType: "TABLE_TOP",
        textureIdField: "tableTopTextureId",
        label: "Текстура столешницы",
    },
    {
        compositionMaterialType: "WALL_PANEL",
        textureIdField: "wallPanelTextureId",
        label: "Текстура стеновой панели",
    },
];

/**
 * Временный scale (мм на один повтор текстуры на 3D), пока бэк не отдаёт scale
 * в композиции или в PictureDto. Меняйте значения для проверки на «Сцена 3D».
 */
export const COMPOSITION_TEXTURE_SCALE_MM_HARDCODE = {
    TABLE_TOP: 300,
};

/** Scale для детали на сцене композиции по compositionMaterialType. */
export const getCompositionTextureScaleMmForDetail = (detail) => {
    const typeKey = detail?.compositionMaterialType;
    if (typeKey != null && COMPOSITION_TEXTURE_SCALE_MM_HARDCODE[typeKey] != null) {
        return COMPOSITION_TEXTURE_SCALE_MM_HARDCODE[typeKey];
    }
    return DEFAULT_TEXTURE_SCALE_MM;
};

const slotByMaterialType = Object.fromEntries(
    COMPOSITION_TEXTURE_SLOTS.map((slot) => [slot.compositionMaterialType, slot]),
);

export const getCompositionMaterialTypeLabel = (typeKey) =>
    compositionMaterialType[typeKey] ?? typeKey;

const normalizeCompositionTextureId = (raw) => {
    if (raw == null || raw === "") {
        return null;
    }
    const num = Number(raw);
    if (!Number.isFinite(num) || num === 0) {
        return null;
    }
    return num;
};

/** Если свой слот пуст — взять парный верх/низ (иначе модули «низа» без текстуры при заполненном только «верха»). */
const COMPOSITION_TEXTURE_TYPE_FALLBACKS = {
    BODY_TOP: ["BODY_BOTTOM"],
    BODY_BOTTOM: ["BODY_TOP"],
    FRONT_TOP: ["FRONT_BOTTOM"],
    FRONT_BOTTOM: ["FRONT_TOP"],
    BACK_TOP: ["BACK_BOTTOM"],
    BACK_BOTTOM: ["BACK_TOP"],
};

export const pickCompositionTextureIdsFromComposition = (composition) => {
    const out = {};
    for (const {textureIdField} of COMPOSITION_TEXTURE_SLOTS) {
        out[textureIdField] = normalizeCompositionTextureId(composition?.[textureIdField]);
    }
    return out;
};

/**
 * Слоты текстур для 3D каталога: GET /3d (merged first) с fallback на GET карточки.
 */
export const resolveCompositionTexturesForCustomer3dScene = (
    sceneComposition,
    fallbackComposition = null,
) => {
    const fallback = pickCompositionTextureIdsFromComposition(fallbackComposition);
    if (sceneComposition == null) {
        return fallback;
    }
    const fromScene = pickCompositionTextureIdsFromComposition(sceneComposition);
    const merged = {...fallback};
    for (const {textureIdField} of COMPOSITION_TEXTURE_SLOTS) {
        const id = normalizeCompositionTextureId(fromScene[textureIdField]);
        if (id != null) {
            merged[textureIdField] = id;
        }
    }
    return merged;
};

/** Уникальные id текстур из слотов композиции (для GET by-textured/by-ids). */
export const collectUniqueCompositionTextureIds = (compositionTextures) => {
    if (compositionTextures == null || typeof compositionTextures !== "object") {
        return [];
    }
    const ids = new Set();
    for (const {textureIdField} of COMPOSITION_TEXTURE_SLOTS) {
        const id = normalizeCompositionTextureId(compositionTextures[textureIdField]);
        if (id != null) {
            ids.add(Number(id));
        }
    }
    return [...ids].filter((id) => Number.isFinite(id));
};

export const getCompositionTextureSignature = (compositionTextures) => (
    COMPOSITION_TEXTURE_SLOTS
        .map(({textureIdField}) => `${textureIdField}:${compositionTextures?.[textureIdField] ?? ""}`)
        .join("|")
);

/** Id текстуры композиции для детали по compositionMaterialType. */
export const pickCompositionTextureIdForDetail = (detail, compositionTextures) => {
    const typeKey = detail?.compositionMaterialType;
    if (typeKey == null || typeKey === "" || !compositionTextures) {
        return null;
    }
    const slot = slotByMaterialType[typeKey];
    if (!slot) {
        return null;
    }
    const direct = normalizeCompositionTextureId(compositionTextures[slot.textureIdField]);
    if (direct != null) {
        return direct;
    }
    for (const fallbackType of COMPOSITION_TEXTURE_TYPE_FALLBACKS[typeKey] || []) {
        const fallbackSlot = slotByMaterialType[fallbackType];
        if (!fallbackSlot) {
            continue;
        }
        const fallbackId = normalizeCompositionTextureId(
            compositionTextures[fallbackSlot.textureIdField],
        );
        if (fallbackId != null) {
            return fallbackId;
        }
    }
    return null;
};

/** Поле name* материала на «Основные» → id текстуры композиции. */
export const COMPOSITION_TEXTURE_FIELD_BY_MATERIAL_NAME = {
    nameBodyTop: "bodyTopTextureId",
    nameBodyBottom: "bodyBottomTextureId",
    nameFrontTop: "frontTopTextureId",
    nameFrontBottom: "frontBottomTextureId",
    nameBackTop: "backTopTextureId",
    nameBackBottom: "backBottomTextureId",
    nameTableTop: "tableTopTextureId",
    nameWallPanel: "wallPanelTextureId",
};

export const findCompositionTextureSlotByMaterialNameField = (nameField) => {
    const textureIdField = COMPOSITION_TEXTURE_FIELD_BY_MATERIAL_NAME[nameField];
    if (!textureIdField) {
        return null;
    }
    return COMPOSITION_TEXTURE_SLOTS.find((slot) => slot.textureIdField === textureIdField) ?? null;
};
